/** Safety inspection forms shown as dashboard cards (admin / super admin / supervisors). */

import { PROJECT_MANAGER_ROLE } from '../utils/roles';

export const FORM_FREQUENCY = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
  AS_NEEDED: 'as_needed',
};

/** Sidebar frequency tabs for supervisor / worker form navigation. */
export const FORM_FREQUENCY_TABS = [
  { id: FORM_FREQUENCY.DAILY, label: 'Daily' },
  { id: FORM_FREQUENCY.WEEKLY, label: 'Weekly' },
  { id: FORM_FREQUENCY.MONTHLY, label: 'Monthly' },
  { id: FORM_FREQUENCY.YEARLY, label: 'Yearly' },
  { id: FORM_FREQUENCY.AS_NEEDED, label: 'Other forms' },
];

/**
 * Frequency schedule (supervisor list):
 * - Excavation inspection – daily when excavating
 * - Fire extinguisher inspection – once per month
 * - First aid Record – for every first aid injury
 * - FLHA – daily
 * - Orientation – for all workers on the first day they come to site
 * - JLG inspection – when they use the JLG
 * - Weekly site inspection – weekly
 * - ECP for Silica – when needed
 * - Create a fall protection plan – when needed
 * - H&S acknowledgement / SWP signoff – yearly
 * - Confined space entry plan – when needed
 */
const FORM_FREQUENCY_BY_INDEX = {
  0: FORM_FREQUENCY.DAILY, // Orientation – first day on site
  1: FORM_FREQUENCY.DAILY, // Excavation inspection – daily when excavating
  2: FORM_FREQUENCY.MONTHLY, // Fire extinguisher – once per month
  3: FORM_FREQUENCY.AS_NEEDED, // JLG – when they use the JLG
  4: FORM_FREQUENCY.AS_NEEDED, // Office & Shop
  5: FORM_FREQUENCY.AS_NEEDED, // Pre job Hazard Assessment
  6: FORM_FREQUENCY.AS_NEEDED, // Prime Contractor Checklist
  7: FORM_FREQUENCY.WEEKLY, // Weekly site inspection
  8: FORM_FREQUENCY.AS_NEEDED, // First aid Record
  9: FORM_FREQUENCY.DAILY, // FLHA – daily
  10: FORM_FREQUENCY.AS_NEEDED, // ECP for Silica
  11: FORM_FREQUENCY.AS_NEEDED, // Fall protection plan
  12: FORM_FREQUENCY.YEARLY, // H&S acknowledgement
  13: FORM_FREQUENCY.YEARLY, // SWP signoff sheet
  14: FORM_FREQUENCY.AS_NEEDED, // Confined space entry plan
};

/** Forms available to Project Manager (same UX as supervisor, filtered set). */
export const PROJECT_MANAGER_FORM_INDICES = [5, 12, 13, 10, 11, 14];

/** Forms available to User/Worker. */
export const WORKER_FORM_INDICES = [12, 13];

export function getSafetyFormCards(projectName = 'HINDBO') {
  return [
    {
      id: 'form-0',
      index: 0,
      title: 'New and/or Young Worker Site Orientation',
      description: 'Site orientation checklist for new and young workers.',
      icon: '📋',
      frequency: FORM_FREQUENCY_BY_INDEX[0],
      frequencyNote: 'For all workers on the first day they come to site',
    },
    {
      id: 'form-1',
      index: 1,
      title: `Excavation Inspection ${projectName}`,
      description: 'Daily excavation and trench safety inspection.',
      icon: '⛏️',
      frequency: FORM_FREQUENCY_BY_INDEX[1],
      frequencyNote: 'Daily when excavating',
    },
    {
      id: 'form-2',
      index: 2,
      title: 'Fire Extinguisher Inspection Log',
      description: 'Monthly fire extinguisher inspection records.',
      icon: '🧯',
      frequency: FORM_FREQUENCY_BY_INDEX[2],
      frequencyNote: 'Once per month',
    },
    {
      id: 'form-3',
      index: 3,
      title: `JLG Pre-Use Inspection ${projectName}`,
      description: 'Aerial lift pre-use inspection checklist.',
      icon: '🏗️',
      frequency: FORM_FREQUENCY_BY_INDEX[3],
      frequencyNote: 'When they use the JLG',
    },
    {
      id: 'form-4',
      index: 4,
      title: `Office & Shop Inspection ${projectName}`,
      description: 'Office and shop workplace safety inspection.',
      icon: '🏬',
      frequency: FORM_FREQUENCY_BY_INDEX[4],
      frequencyNote: 'When needed',
    },
    {
      id: 'form-5',
      index: 5,
      title: `Pre job Hazard Assessment - ${projectName}`,
      description: 'Pre-job hazard identification and controls.',
      icon: '⚠️',
      frequency: FORM_FREQUENCY_BY_INDEX[5],
      frequencyNote: 'Once at beginning of project or if the scope of work changes',
    },
    {
      id: 'form-6',
      index: 6,
      title: `Prime Contractor Worksite Checklist-${projectName}`,
      description: 'Prime contractor worksite compliance checklist.',
      icon: '✅',
      frequency: FORM_FREQUENCY_BY_INDEX[6],
      frequencyNote: 'When needed',
    },
    {
      id: 'form-7',
      index: 7,
      title: `Weekly Site Inspection ${projectName}`,
      description: 'Weekly site safety walkthrough inspection.',
      icon: '📅',
      frequency: FORM_FREQUENCY_BY_INDEX[7],
      frequencyNote: 'Weekly',
    },
    {
      id: 'form-8',
      index: 8,
      title: `First Aid Record ${projectName}`,
      description: 'First aid treatment and incident records.',
      icon: '🩹',
      frequency: FORM_FREQUENCY_BY_INDEX[8],
      frequencyNote: 'For every first aid injury',
    },
    {
      id: 'form-9',
      index: 9,
      title: 'Field Level Hazard Assessment',
      description: 'Field-level hazard assessment (FLHA) for tasks.',
      icon: '📊',
      frequency: FORM_FREQUENCY_BY_INDEX[9],
      frequencyNote: 'Daily',
    },
    {
      id: 'form-10',
      index: 10,
      title: 'ECP for Silica cutting-grinding',
      description: 'Exposure control plan for cutting, grinding, and polishing silica-containing stone.',
      icon: '🪨',
      frequency: FORM_FREQUENCY_BY_INDEX[10],
      frequencyNote: 'When they need a silica plan',
    },
    {
      id: 'form-11',
      index: 11,
      title: 'Creating a fall protection plan for your worksite',
      description: 'Fillable template',
      icon: '🦺',
      frequency: FORM_FREQUENCY_BY_INDEX[11],
      frequencyNote: 'When they need a fall protection plan',
    },
    {
      id: 'form-12',
      index: 12,
      title: 'H&S Acknowledgement',
      description: 'Health and safety acknowledgement for workers.',
      icon: '📝',
      frequency: FORM_FREQUENCY_BY_INDEX[12],
      frequencyNote: 'Once per year',
    },
    {
      id: 'form-13',
      index: 13,
      title: 'SWP Signoff Sheet',
      description: 'Safe work procedure signoff sheet for workers.',
      icon: '✍️',
      frequency: FORM_FREQUENCY_BY_INDEX[13],
      frequencyNote: 'Once per year',
    },
    {
      id: 'form-14',
      index: 14,
      title: 'Confined Space Entry Plan',
      description: 'Confined space entry plan for the worksite.',
      icon: '🚪',
      frequency: FORM_FREQUENCY_BY_INDEX[14],
      frequencyNote: 'When they need a confined space entry plan',
    },
  ];
}

export function getSafetyFormCardsForRole(role, projectName = 'HINDBO') {
  const cards = getSafetyFormCards(projectName);
  const byIndex = new Map(cards.map((card) => [card.index, card]));
  if (role === PROJECT_MANAGER_ROLE) {
    return PROJECT_MANAGER_FORM_INDICES.map((index) => byIndex.get(index)).filter(Boolean);
  }
  if (role === 'user') {
    return WORKER_FORM_INDICES.map((index) => byIndex.get(index)).filter(Boolean);
  }
  return cards;
}

export function getSafetyFormCardsByFrequency(projectName = 'HINDBO', frequency, role = null) {
  const cards = role
    ? getSafetyFormCardsForRole(role, projectName)
    : getSafetyFormCards(projectName);
  if (!frequency) return cards;
  return cards.filter((card) => card.frequency === frequency);
}

export function getSafetyFormTabs(projectName = 'HINDBO') {
  return getSafetyFormCards(projectName).map((c) => c.title);
}

export function isSafetyFormView(viewId) {
  return typeof viewId === 'string' && viewId.startsWith('form-');
}

export function parseSafetyFormIndex(viewId) {
  if (!isSafetyFormView(viewId)) return null;
  const index = parseInt(viewId.replace('form-', ''), 10);
  return Number.isNaN(index) ? null : index;
}

/** Short PDF filenames for user form exports (users/{username}/). */
const FORM_PDF_NAMES = [
  'Site Worker Orientation',
  'Excavation Inspection',
  'Fire Extinguisher Inspection Log',
  'JLG Pre-Use Inspection',
  'Office Shop Inspection',
  'Pre Job Hazard Assessment',
  'Prime Contractor Checklist',
  'Weekly Site Inspection',
  'First Aid Record',
  'Field Level Hazard Assessment',
  'ECP for Silica cutting-grinding',
  'Creating a fall protection plan for your worksite',
  'HS Acknowledgement',
  'SWP Signoff Sheet',
  'Confined Space Entry Plan',
];

export function getFormPdfFileName(formIndex) {
  const name = FORM_PDF_NAMES[formIndex];
  if (!name) return 'Safety Form.pdf';
  return `${name}.pdf`;
}
